import React from 'react';
import { ShoppingCart, ArrowRight, Check, Heart, Flame, Star } from 'lucide-react';
import {
  type OrderButtonConfig,
  type IconType,
  getOrderButtonStyles,
} from '../services/orderButtonDesign';

interface OrderButtonProps {
  /** Configuration depuis l'admin (ou la config par défaut) */
  config: OrderButtonConfig;
  /** Texte fallback (utilisé si customText vide) */
  fallbackText: string;
  /** Prix à afficher (utilisé si showPrice = true) */
  price?: string;
  /** Bouton désactivé */
  disabled?: boolean;
  /** Callback au clic */
  onClick: () => void;
  /** Classes additionnelles (pour les cas où enabled = false dans la config) */
  defaultClassName?: string;
}

const ICON_MAP: Record<Exclude<IconType, 'none'>, React.FC<{ size?: number }>> = {
  cart: ShoppingCart,
  arrow: ArrowRight,
  check: Check,
  heart: Heart,
  flame: Flame,
  star: Star,
};

/**
 * v5 — Bouton Commander avec design personnalisable.
 *
 * Si config.enabled = false, retourne un bouton classique (style par défaut).
 * Si config.enabled = true, applique tous les styles custom de la config.
 */
export const OrderButton: React.FC<OrderButtonProps> = ({
  config,
  fallbackText,
  price,
  disabled,
  onClick,
  defaultClassName,
}) => {
  // Texte affiché : customText > fallback
  const baseText = config.customText.trim() || fallbackText;
  const displayText =
    config.showPrice && price ? `${baseText} · ${price}` : baseText;

  // Mode par défaut : design actuel du kiosk
  if (!config.enabled) {
    return (
      <button
        onClick={onClick}
        disabled={disabled}
        className={
          defaultClassName ||
          'w-full py-3 rounded-xl font-semibold text-sm shadow-lg transition-colors bg-orange-500 text-white hover:bg-orange-600 shadow-orange-500/30 disabled:opacity-50 disabled:cursor-not-allowed'
        }
      >
        {displayText}
      </button>
    );
  }

  // Mode custom
  const { style, className } = getOrderButtonStyles(config);
  const IconComp = config.icon !== 'none' ? ICON_MAP[config.icon] : null;
  const iconEl = IconComp ? <IconComp size={18} /> : null;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={className}
      style={style}
    >
      {/* Contenu au-dessus du pseudo-élément shine */}
      <span style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: '8px' }}>
        {iconEl && config.iconPosition === 'left' && iconEl}
        <span>{displayText}</span>
        {iconEl && config.iconPosition === 'right' && iconEl}
      </span>
    </button>
  );
};

export default OrderButton;
